import React, { useEffect, useMemo, useState } from 'react';
import { Download, Eye, MapPin, Plus, Printer, RefreshCcw, Save, Trash2, X } from 'lucide-react';
import { cleanPhone, dateLabel, money, updateOrderStatus, updateOrderTracking, createManualOrder, sendShippedNotification, sendPaymentSuccessWebhook, sendOrderShippedWebhook, professionalCourierTrackingUrl, softDeleteOrder, restoreOrder } from '../lib/compat.js';
import { printShippingLabels } from './labels.js';
import { exportToExcel } from './exports.js';
import { AdminFilterBar, AdminModal, EmptyState, Pager, STATUS_LABELS, STATUS_OPTIONS, orderPhone, orderStatus, orderStudent, orderTotal, totalUnits, usePagedRows } from './shared.jsx';

function inPeriod(value, period, from, to) {
  if (!period && !from && !to) return true;
  const date = value?.toDate ? value.toDate() : new Date(value || '');
  if (isNaN(date.getTime())) return false;
  if (period && period !== 'custom') {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - Math.max(Number(period) - 1, 0));
    cutoff.setHours(0, 0, 0, 0);
    return date >= cutoff;
  }
  if (from) { const min = new Date(from); min.setHours(0, 0, 0, 0); if (date < min) return false; }
  if (to) { const max = new Date(to); max.setHours(23, 59, 59, 999); if (date > max) return false; }
  return true;
}

function webhookStatusText(status, code) {
  if (!status) return 'Not sent yet';
  const clean = String(status);
  return `${clean.charAt(0).toUpperCase()}${clean.slice(1)}${code ? ` (${code})` : ''}`;
}

function webhookStatusColor(status) {
  if (status === 'sent') return 'var(--admin-green)';
  if (status === 'failed') return 'var(--admin-red)';
  if (status === 'skipped') return 'var(--admin-muted)';
  return 'var(--admin-text)';
}

export default function AdminOrders({ data, query, onRefresh, title = 'Orders' }) {
  const [filters, setFilters] = useState({ view: 'active', status: '', type: '', book: '', period: '', from: '', to: '' });
  const [selected, setSelected] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [bulkMsg, setBulkMsg] = useState('');
  const [webhookMsg, setWebhookMsg] = useState('');
  const needle = query.trim().toLowerCase();
  const isTrashView = filters.view === 'trash';
  const trashCount = data.trashedOrders?.length || 0;
  const sourceOrders = isTrashView ? (data.trashedOrders || []) : (data.orders || []);

  const bookOptions = useMemo(() => [...new Set(data.books.map((b) => b.title).filter(Boolean))].sort(), [data.books]);

  const rows = useMemo(() => sourceOrders.filter((o) => {
    const text = [o.orderId, o.paymentId, orderStudent(o), orderPhone(o)].join(' ').toLowerCase();
    const status = orderStatus(o);
    const type = o.isManual || o.source === 'manual_entry' ? 'manual' : 'online';
    const bookText = (o.items || []).map((i) => [i.bt, i.bookTitle, i.vn, i.variantName].join(' ')).join(' ').toLowerCase();
    return (!needle || text.includes(needle)) &&
      (!filters.status || status === filters.status || (filters.status === 'processing' && status === 'paid')) &&
      (!filters.type || type === filters.type) &&
      (!filters.book || bookText.includes(filters.book.toLowerCase())) &&
      inPeriod(o.createdAt, filters.period, filters.from, filters.to);
  }), [sourceOrders, filters, needle]);

  const pager = usePagedRows(rows, 50);

  // Order stats
  const stats = useMemo(() => ({
    revenue: rows.reduce((s, o) => s + Number(o.total || orderTotal(o) || 0), 0),
    processing: rows.filter((o) => ['processing', 'paid'].includes(orderStatus(o))).length,
    delivered: rows.filter((o) => orderStatus(o) === 'delivered').length
  }), [rows]);

  async function changeStatus(orderId, status) {
    await updateOrderStatus(orderId, status);
    await onRefresh();
  }

  async function saveTracking(orderId, trackingNumber) {
    const trackingPayload = await updateOrderTracking(orderId, trackingNumber);
    const order = data.orders.find((o) => o.id === orderId);
    if (order) {
      const phone = orderPhone(order);
      if (phone) {
        await sendShippedNotification(phone, {
          ...order,
          ...trackingPayload,
          deliveryStatus: 'shipped'
        });
      }
    }
    await onRefresh();
    return trackingPayload;
  }

  async function deleteOrder(order) {
    const ok = window.confirm(`Move order ${order.orderId || order.id} to trash? It will be hidden from reports and student order history.`);
    if (!ok) return;
    await softDeleteOrder(order.id);
    setSelected((current) => current.filter((id) => id !== order.id));
    if (detail?.id === order.id) setDetail(null);
    await onRefresh();
  }

  async function restoreTrashedOrder(order) {
    await restoreOrder(order.id);
    setSelected((current) => current.filter((id) => id !== order.id));
    if (detail?.id === order.id) setDetail(null);
    await onRefresh();
  }

  async function bulkMoveToTrash() {
    if (!selected.length) return;
    const ok = window.confirm(`Move ${selected.length} selected order(s) to trash?`);
    if (!ok) return;
    await Promise.all(selected.map((id) => softDeleteOrder(id)));
    setSelected([]);
    await onRefresh();
  }

  async function bulkRestore() {
    if (!selected.length) return;
    await Promise.all(selected.map((id) => restoreOrder(id)));
    setSelected([]);
    await onRefresh();
  }

  async function bulkShip() {
    setBulkMsg('');
    try {
      await Promise.all(selected.map(async (id) => {
        await updateOrderStatus(id, 'shipped');
      }));
      setSelected([]);
      setBulkMsg(`Marked ${selected.length} order(s) as shipped. Add tracking number from order details to send WhatsApp webhook.`);
      await onRefresh();
    } catch (e) {
      setBulkMsg('Error: ' + e.message);
    }
  }

  function doExcelExport() {
    exportToExcel('muppadai-orders-' + new Date().toISOString().slice(0, 10), rows.map((o) => ({
      'Order ID': o.orderId || o.id,
      'Payment ID': o.paymentId || '',
      'Date': dateLabel(o.createdAt),
      'Student': orderStudent(o),
      'Phone': orderPhone(o),
      'Total': Number(o.total || orderTotal(o) || 0),
      'Status': orderStatus(o),
      'Type': o.isManual ? 'Manual' : 'Online',
      'Items': (o.items || []).map((i) => `${i.bt || i.bookTitle} ${i.vn || i.variantName} x${i.qty}`).join(' | '),
      'Address': o.delivery?.address || '',
      'District': o.delivery?.district || '',
      'State': o.delivery?.state || ''
    })), 'Orders');
  }

  async function resendRecentWebhooks() {
    setWebhookMsg('');
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 2);
    const recent = data.orders.filter((order) => {
      const value = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt || '');
      return !isNaN(value.getTime()) && value >= cutoff;
    });
    if (!recent.length) {
      setWebhookMsg('No orders found in the last 2 days.');
      return;
    }
    await Promise.all(recent.map(async (order) => {
      await sendPaymentSuccessWebhook(order);
      if (orderStatus(order) === 'shipped') {
        await sendOrderShippedWebhook({
          ...order,
          deliveryStatus: 'shipped',
          shippedAt: order.shippedAt || new Date().toISOString()
        });
      }
    }));
    setWebhookMsg(`Webhook resent for ${recent.length} recent order(s).`);
  }

  const selectedOrders = rows.filter((o) => selected.includes(o.id));
  const allPageSelected = !!pager.paged.length && pager.paged.every((o) => selected.includes(o.id));

  return (
    <>
      {/* Order summary stats */}
      <div className="adminStatsGrid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 16 }}>
        <div className="statCard yellow"><div className="sc-lbl">Total Revenue</div><div className="sc-val">{money(stats.revenue)}</div></div>
        <div className="statCard"><div className="sc-lbl">Total Orders</div><div className="sc-val">{rows.length}</div></div>
        <div className="statCard"><div className="sc-lbl">Ready to Ship</div><div className="sc-val">{stats.processing}</div></div>
        <div className="statCard green"><div className="sc-lbl">Delivered</div><div className="sc-val">{stats.delivered}</div></div>
      </div>

      {/* Filter bar */}
      <AdminFilterBar>
        <div className="filterGroup">
          <label>View</label>
          <select value={filters.view} onChange={(e) => { setSelected([]); setFilters({ ...filters, view: e.target.value }); }}>
            <option value="active">Active Orders</option>
            <option value="trash">Trash ({trashCount})</option>
          </select>
        </div>
        <div className="filterGroup">
          <label>Status</label>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All Status</option>
            <option value="processing">✅ Payment Received</option>
            <option value="shipped">🚚 Shipped</option>
            <option value="delivered">📦 Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="filterGroup">
          <label>Type</label>
          <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="">All Types</option>
            <option value="manual">📝 Manual</option>
            <option value="online">🌐 Online</option>
          </select>
        </div>
        <div className="filterGroup">
          <label>Book</label>
          <select value={filters.book} onChange={(e) => setFilters({ ...filters, book: e.target.value })}>
            <option value="">All Books</option>
            {bookOptions.map((b) => <option value={b} key={b}>{b}</option>)}
          </select>
        </div>
        <div className="filterGroup">
          <label>Period</label>
          <select value={filters.period} onChange={(e) => setFilters({ ...filters, period: e.target.value })}>
            <option value="">All Time</option>
            <option value="1">Today</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        {filters.period === 'custom' && (
          <>
            <div className="filterGroup"><label>From</label><input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} /></div>
            <div className="filterGroup"><label>To</label><input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} /></div>
          </>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => { setSelected([]); setFilters({ view: 'active', status: '', type: '', book: '', period: '', from: '', to: '' }); }}>Clear</button>
          {!isTrashView && <button type="button" onClick={resendRecentWebhooks}><RefreshCcw size={14} /> Resend 2 Days Webhook</button>}
          {!isTrashView && <button type="button" className="btn-green" onClick={() => setManualOpen(true)}><Plus size={14} /> Manual Entry</button>}
          <button type="button" className="btn-export-xl" onClick={doExcelExport}><Download size={14} /> Excel</button>
          <button type="button" className="btn-export-pdf" onClick={() => window.print()}><Download size={14} /> PDF</button>
        </div>
      </AdminFilterBar>
      {webhookMsg && <div className="banner compact">{webhookMsg}</div>}

      {/* Bulk action bar */}
      {!!selected.length && (
        <div className="bulkBar">
          <strong>{selected.length} selected</strong>
          <button type="button" className="btn-primary" onClick={bulkShip}>🚚 Mark Selected as Shipped</button>
          <span style={{ fontSize: 11, color: 'var(--admin-muted)' }}>Sends push notification to student</span>
          <button type="button" className="btn-green" onClick={() => printShippingLabels(selectedOrders)}><Printer size={14} /> Print Labels</button>
          {!isTrashView && <button type="button" onClick={bulkMoveToTrash} style={{ color: 'var(--admin-red)' }}><Trash2 size={14} /> Move to Trash</button>}
          {bulkMsg && <span style={{ fontSize: 12, color: 'var(--admin-green)' }}>{bulkMsg}</span>}
        </div>
      )}

      {/* Table */}
      <div className="adminCard">
        <div className="adminCardHead">
          <h3>{title} <span style={{ color: 'var(--admin-muted)', fontWeight: 400, fontSize: 12 }}>Showing {pager.paged.length} of {rows.length}</span></h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: 'var(--admin-muted)' }}>Show
              <select value={pager.pageSize} onChange={(e) => pager.setPageSize(Number(e.target.value))} style={{ marginLeft: 6 }}>
                {[10, 20, 50, 100].map((n) => <option value={n} key={n}>{n}</option>)}
                <option value={9999}>All</option>
              </select>
            </label>
          </div>
        </div>
        <div className="tableWrap ordersTableWrap">
          <table className="ordersTable">
            <colgroup>
              <col className="colSelect" />
              <col className="colOrder" />
              <col className="colStudent" />
              <col className="colBooks" />
              <col className="colTotal" />
              <col className="colStatus" />
              <col className="colTracking" />
              <col className="colActions" />
            </colgroup>
            <thead>
              <tr>
                <th><input type="checkbox" disabled={isTrashView} checked={!isTrashView && allPageSelected} onChange={(e) => setSelected(e.target.checked ? [...new Set([...selected, ...pager.paged.map((o) => o.id)])] : selected.filter((id) => !pager.paged.some((o) => o.id === id)))} /></th>
                <th>Order / Payment</th>
                <th>Student</th>
                <th>Books</th>
                <th>Total</th>
                <th>Status</th>
                <th>Tracking</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pager.paged.map((order) => {
                const status = orderStatus(order);
                return (
                  <tr key={order.id}>
                    <td className="colSelect" data-label="Select"><input type="checkbox" disabled={isTrashView} checked={!isTrashView && selected.includes(order.id)} onChange={(e) => setSelected(e.target.checked ? [...selected, order.id] : selected.filter((id) => id !== order.id))} /></td>
                    <td data-label="Order">
                      <strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{order.orderId || order.id}</strong>
                      {order.paymentId && <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{order.paymentId}</div>}
                      <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{dateLabel(order.createdAt)}</div>
                      {order.isManual && <span className="badge b-manual">Manual</span>}
                    </td>
                    <td data-label="Student">
                      <strong>{orderStudent(order)}</strong>
                      <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{orderPhone(order)}</div>
                    </td>
                    <td className="ordersBooksCell" data-label="Books" style={{ fontSize: 12 }}>
                      {(order.items || []).slice(0, 2).map((i, idx) => <div key={idx}>{i.bt || i.bookTitle} — {i.vn || i.variantName} ×{i.qty || 1}</div>)}
                      {(order.items || []).length > 2 && <div style={{ color: 'var(--admin-muted)' }}>+{order.items.length - 2} more</div>}
                    </td>
                    <td data-label="Total"><strong>{money(Number(order.total || orderTotal(order) || 0))}</strong></td>
                    <td data-label="Status">
                      <select
                        className="statusSelect"
                        data-status={status}
                        value={status}
                        disabled={isTrashView}
                        onChange={(e) => changeStatus(order.id, e.target.value)}
                      >
                        <option value="processing">✅ Payment Received</option>
                        <option value="shipped">🚚 Shipped</option>
                        <option value="delivered">📦 Delivered</option>
                        <option value="cancelled">❌ Cancelled</option>
                      </select>
                    </td>
                    <td data-label="Tracking">
                      {isTrashView ? (
                        <span style={{ color: 'var(--admin-muted)', fontSize: 12 }}>{order.trackingNumber || 'Trashed'}</span>
                      ) : (
                        <TrackingCell order={order} onSave={saveTracking} />
                      )}
                    </td>
                    <td data-label="Actions">
                      <div className="rowActions">
                        <button type="button" onClick={() => setDetail(order)}><Eye size={13} /> View</button>
                        {!isTrashView && <button type="button" onClick={() => printShippingLabels([order])}><Printer size={13} /> Label</button>}
                        {isTrashView ? (
                          <button type="button" onClick={() => restoreTrashedOrder(order)}><RefreshCcw size={13} /> Restore</button>
                        ) : (
                          <button type="button" onClick={() => deleteOrder(order)} style={{ color: 'var(--admin-red)' }}><Trash2 size={13} /> Trash</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length && <EmptyState title={isTrashView ? 'Trash is empty' : 'No orders found'} body={isTrashView ? 'Deleted orders will appear here.' : 'Try adjusting your filters.'} />}
        </div>
        <Pager state={pager} total={rows.length} />
      </div>

      {manualOpen && <ManualOrderModal books={data.books} onClose={() => setManualOpen(false)} onSaved={async () => { setManualOpen(false); await onRefresh(); }} />}
      {detail && <OrderDetailModal order={detail} trashView={isTrashView} onClose={() => setDetail(null)} onDelete={deleteOrder} onRestore={restoreTrashedOrder} onStatusChange={async (status) => { await changeStatus(detail.id, status); setDetail({ ...detail, deliveryStatus: status }); }} onTrackingSave={async (trackingNumber) => { const payload = await saveTracking(detail.id, trackingNumber); setDetail({ ...detail, ...payload }); }} />}
    </>
  );
}

function TrackingCell({ order, onSave }) {
  const [value, setValue] = useState(order.trackingNumber || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const savedValue = String(order.trackingNumber || '').trim();
  const clean = String(value || '').trim();

  useEffect(() => {
    setValue(order.trackingNumber || '');
    setMessage('');
  }, [order.id, order.trackingNumber]);

  async function saveInlineTracking() {
    if (!clean) {
      setMessage('Enter tracking no.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      await onSave(order.id, clean);
      setMessage('Saved');
    } catch (error) {
      setMessage(error.message || 'Not saved');
    } finally {
      setSaving(false);
    }
  }

  function openTracking() {
    if (!clean) return;
    window.open(professionalCourierTrackingUrl(clean), '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="trackingInline">
      <input
        value={value}
        onChange={(event) => setValue(event.target.value.trim())}
        placeholder="Tracking no."
        aria-label={`Tracking number for ${order.orderId || order.id}`}
      />
      <button
        type="button"
        className="trackingIconButton save"
        onClick={saveInlineTracking}
        disabled={saving || !clean || clean === savedValue}
        title="Save tracking number"
        aria-label="Save tracking number"
      >
        <Save size={14} />
      </button>
      <button
        type="button"
        className="trackingIconButton track"
        onClick={openTracking}
        disabled={!clean}
        title="Track parcel"
        aria-label="Track parcel"
      >
        <MapPin size={14} />
      </button>
      {message && <span className={message === 'Saved' ? 'trackingInlineMsg ok' : 'trackingInlineMsg'}>{message}</span>}
    </div>
  );
}

function OrderDetailModal({ order, trashView = false, onClose, onDelete, onRestore, onStatusChange, onTrackingSave }) {
  const [status, setStatus] = useState(orderStatus(order));
  const [trackingNumber, setTrackingNumber] = useState(order.trackingNumber || '');
  const [saving, setSaving] = useState(false);
  const [trackingMsg, setTrackingMsg] = useState('');

  async function save() {
    setSaving(true);
    await onStatusChange(status);
    setSaving(false);
  }

  async function saveTrackingNumber() {
    setSaving(true);
    setTrackingMsg('');
    try {
      await onTrackingSave(trackingNumber);
      setStatus('shipped');
      setTrackingMsg('Tracking saved and shipped webhook triggered.');
    } catch (error) {
      setTrackingMsg('Error: ' + (error.message || 'Unable to save tracking.'));
    } finally {
      setSaving(false);
    }
  }

  function openTracking() {
    const clean = String(trackingNumber || '').trim();
    if (!clean) return;
    window.open(professionalCourierTrackingUrl(clean), '_blank', 'noopener,noreferrer');
  }

  return (
    <AdminModal title={`Order Details — ${order.orderId || order.id}`} onClose={onClose} wide>
      <div className="detailGrid">
        <div><span>Order ID</span><strong>{order.orderId || order.id}</strong></div>
        <div><span>Payment ID</span><strong>{order.paymentId || '—'}</strong></div>
        <div><span>Date</span><strong>{dateLabel(order.createdAt)}</strong></div>
        <div><span>Type</span><strong>{order.isManual ? '📝 Manual' : '🌐 Online'}</strong></div>
        <div><span>Customer</span><strong>{orderStudent(order)}</strong></div>
        <div><span>Phone</span><strong>{orderPhone(order) || '—'}</strong></div>
        {order.delivery?.altPhone && <div><span>Alt Phone</span><strong>{order.delivery.altPhone}</strong></div>}
        {order.delivery?.email && <div><span>Email</span><strong>{order.delivery.email}</strong></div>}
        <div><span>Total</span><strong>{money(Number(order.total || orderTotal(order) || 0))}</strong></div>
        <div><span>Items</span><strong>{totalUnits(order)}</strong></div>
        <div><span>Tracking</span><strong>{trackingNumber || 'Not added'}</strong></div>
        <div>
          <span>Payment Webhook</span>
          <strong style={{ color: webhookStatusColor(order.paymentWebhookStatus) }}>
            {webhookStatusText(order.paymentWebhookStatus, order.paymentWebhookStatusCode)}
          </strong>
        </div>
        <div>
          <span>Shipped Webhook</span>
          <strong style={{ color: webhookStatusColor(order.shippedWebhookStatus) }}>
            {webhookStatusText(order.shippedWebhookStatus, order.shippedWebhookStatusCode)}
          </strong>
        </div>
      </div>
      <div className="modalSection">
        <h4>Books Ordered</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr><th style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid var(--admin-border)' }}>Book</th><th style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid var(--admin-border)' }}>Variant</th><th style={{ textAlign: 'right', padding: '4px 6px', borderBottom: '1px solid var(--admin-border)' }}>Qty</th><th style={{ textAlign: 'right', padding: '4px 6px', borderBottom: '1px solid var(--admin-border)' }}>Price</th></tr></thead>
          <tbody>
            {(order.items || []).map((item, i) => (
              <tr key={i}>
                <td style={{ padding: '5px 6px' }}>{item.bt || item.bookTitle}</td>
                <td style={{ padding: '5px 6px', color: 'var(--admin-muted)' }}>{item.vn || item.variantName}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right' }}>{item.qty || 1}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right' }}>{money(Number(item.price || 0) * Number(item.qty || 1))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="modalSection">
        <h4>Delivery Address</h4>
        <p style={{ fontSize: 13, lineHeight: 1.6 }}>{order.delivery?.address || [order.delivery?.street, order.delivery?.postOffice, order.delivery?.district, order.delivery?.state].filter(Boolean).join(', ') || '—'}</p>
      </div>
      {!trashView && <>
      <div className="modalSection">
        <h4>Professional Courier Tracking</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'center' }}>
          <input
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value.trim())}
            placeholder="Enter tracking number"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--admin-border)', borderRadius: 8, fontFamily: 'monospace' }}
          />
          <button type="button" className="btn-green" onClick={saveTrackingNumber} disabled={saving || !trackingNumber.trim()}>
            Save & Send Shipped
          </button>
          <button type="button" className="trackingIconButton track" onClick={openTracking} disabled={!trackingNumber.trim()} title="Track parcel" aria-label="Track parcel">
            <MapPin size={14} />
          </button>
        </div>
        <p style={{ margin: '8px 0 0', color: 'var(--admin-muted)', fontSize: 12 }}>
          Saving this will mark the order as shipped and trigger the Shipped Notification Webhook with tracking details.
        </p>
        {trackingMsg && <div className="banner compact" style={{ marginBottom: 0 }}>{trackingMsg}</div>}
      </div>
      <div className="modalSection">
        <h4>Update Status</h4>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select className="statusSelect" value={status} onChange={(e) => setStatus(e.target.value)} style={{ flex: 1 }}>
            {STATUS_OPTIONS.map((s) => <option value={s} key={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          <button type="button" className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Update Status'}</button>
        </div>
      </div>
      </>}
      <div className="modalActions">
        {trashView ? (
          <button type="button" className="btn-green" onClick={() => onRestore?.(order)}><RefreshCcw size={14} /> Restore Order</button>
        ) : (
          <button type="button" onClick={() => onDelete?.(order)} style={{ color: 'var(--admin-red)' }}><Trash2 size={14} /> Move to Trash</button>
        )}
        {!trashView && <button type="button" onClick={() => printShippingLabels([order])}><Printer size={14} /> Print Label</button>}
        <button type="button" onClick={onClose}>Close</button>
      </div>
    </AdminModal>
  );
}

function ManualOrderModal({ books, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', phone: '', altPhone: '', email: '',
    street: '', postOffice: '', pincode: '', district: '', state: 'Tamil Nadu',
    paymentId: '', deliveryStatus: 'processing'
  });
  const [items, setItems] = useState([{ bookVariantKey: '', price: '', qty: 1 }]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  // Build combined Book+Variant options
  const bookVariantOptions = useMemo(() => {
    const opts = [];
    books.forEach((book) => {
      (book.variants || []).forEach((v) => {
        if (v.name && v.price) {
          opts.push({
            key: `${book.id}::${v.name || v.vn}`,
            label: `${book.title} — ${v.name || v.vn} (₹${v.price})`,
            bookId: book.id,
            bookTitle: book.title,
            variantName: v.name || v.vn,
            price: Number(v.price || 0)
          });
        }
      });
    });
    return opts;
  }, [books]);

  const total = items.reduce((s, item) => s + Number(item.price || 0) * Number(item.qty || 1), 0);

  function updateItem(idx, patch) {
    setItems((cur) => cur.map((item, i) => i === idx ? { ...item, ...patch } : item));
  }

  function selectBookVariant(idx, key) {
    const opt = bookVariantOptions.find((o) => o.key === key);
    if (opt) {
      updateItem(idx, { bookVariantKey: key, bookId: opt.bookId, bookTitle: opt.bookTitle, variantName: opt.variantName, price: opt.price });
    } else {
      updateItem(idx, { bookVariantKey: '', bookId: '', bookTitle: '', variantName: '', price: '' });
    }
  }

  async function save() {
    setMessage('');
    setBusy(true);
    try {
      const mapped = items.filter((i) => i.bookId).map((i) => ({
        bid: i.bookId,
        bt: i.bookTitle || '',
        bookTitle: i.bookTitle || '',
        vn: i.variantName || '',
        variantName: i.variantName || '',
        price: Number(i.price || 0),
        qty: Number(i.qty || 1)
      }));
      if (!mapped.length) { setMessage('Add at least one book.'); setBusy(false); return; }
      await createManualOrder({ delivery: form, items: mapped, paymentId: form.paymentId, deliveryStatus: form.deliveryStatus });
      await onSaved();
    } catch (e) {
      setMessage(e.message || 'Unable to create order.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminModal title="Create Manual Order" onClose={onClose} wide>
      {message && <div className="banner danger compact">{message}</div>}
      <div className="adminFormGrid">
        <label>Student Name *<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label>Phone *<input value={form.phone} maxLength={10} onChange={(e) => setForm({ ...form, phone: cleanPhone(e.target.value) })} /></label>
        <label>Alternate Phone<input value={form.altPhone} maxLength={10} onChange={(e) => setForm({ ...form, altPhone: cleanPhone(e.target.value) })} /></label>
        <label>Email<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label className="full">Street / Door No / Area *<input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} /></label>
        <label>Post Office / Area<input value={form.postOffice} onChange={(e) => setForm({ ...form, postOffice: e.target.value })} /></label>
        <label>Pincode *<input value={form.pincode} maxLength={6} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })} /></label>
        <label>District *<input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} /></label>
        <label>State<input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></label>
        <label>Payment ID / Reference<input value={form.paymentId} onChange={(e) => setForm({ ...form, paymentId: e.target.value })} /></label>
        <label>Delivery Status
          <select value={form.deliveryStatus} onChange={(e) => setForm({ ...form, deliveryStatus: e.target.value })}>
            {['processing', 'shipped', 'delivered', 'cancelled'].map((s) => <option value={s} key={s}>{s === 'processing' ? 'Payment Received' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </label>
      </div>

      <div className="modalSection">
        <div className="sectionTitle">
          <h4>Books &amp; Quantities</h4>
          <button type="button" onClick={() => setItems([...items, { bookVariantKey: '', price: '', qty: 1 }])}><Plus size={13} /> Add Row</button>
        </div>
        {items.map((item, idx) => (
          <div className="manualItemRow" key={idx}>
            <select
              value={item.bookVariantKey || ''}
              onChange={(e) => selectBookVariant(idx, e.target.value)}
              style={{ flex: 3, minWidth: 0 }}
            >
              <option value="">Select Book &amp; Variant</option>
              {bookVariantOptions.map((opt) => <option value={opt.key} key={opt.key}>{opt.label}</option>)}
            </select>
            <input
              type="number"
              value={item.price}
              placeholder="Price"
              onChange={(e) => updateItem(idx, { price: e.target.value })}
              style={{ width: 80, flexShrink: 0 }}
            />
            <input
              type="number"
              min="1"
              value={item.qty}
              onChange={(e) => updateItem(idx, { qty: e.target.value })}
              style={{ width: 60, flexShrink: 0 }}
            />
            <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))} disabled={items.length === 1}><X size={13} /></button>
          </div>
        ))}
        <div className="adminTotalLine"><span>Total</span><strong>{money(total)}</strong></div>
      </div>

      <div className="modalActions">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Create Order'}</button>
      </div>
    </AdminModal>
  );
}
